import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import Chip from "@/components/ui/chip";
import ConfirmModal from "@/components/ui/confirm-modal";
import Toast from "@/components/ui/toast";
import {
  Actions as ActionsIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type {
  BlackListEntry,
  BlackListFormValues,
} from "../types/black-list.types";
import { blackListApi } from "../services/black-list.api";
import { uploadAdminFiles } from "@/lib/files.api";
import BlackListDetailDrawer from "../components/BlackListDetailDrawer";
import BlackListFormDrawer from "../components/BlackListFormDrawer";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./BlackListPage.module.scss";

const BlackListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.BLACK_LIST_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.BLACK_LIST_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.BLACK_LIST_DELETE);

  const [entries, setEntries] = useState<BlackListEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<BlackListEntry[]>([]);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [sortBy, setSortBy] = useState<
    "startDate" | "endDate" | "bannedAt" | "createdAt"
  >("startDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [detailEntry, setDetailEntry] = useState<BlackListEntry | null>(null);
  const [editEntry, setEditEntry] = useState<BlackListEntry | null>(null);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [confirmRemoveEntry, setConfirmRemoveEntry] =
    useState<BlackListEntry | null>(null);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [toastState, setToastState] = useState<{
    variant: "success" | "fail";
    message: string;
    title?: string;
  } | null>(null);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await blackListApi.getAll({
        page: currentPage,
        pageSize,
        sortBy,
        sortOrder,
        filters: {
          search: searchTerm,
          reason: filterValues.reason as
            | BlackListEntry["reason"]
            | BlackListEntry["reason"][]
            | undefined,
        },
      });
      setEntries(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching black list:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filterValues, sortBy, sortOrder]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleRowSelect = (row: BlackListEntry) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(entries);
    } else {
      setSelectedRows([]);
    }
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleAddToBlackList = () => {
    setIsAddDrawerOpen(true);
  };

  const handleOpenDetail = async (row: BlackListEntry) => {
    try {
      const full = await blackListApi.getById(row.id);
      setDetailEntry(full);
    } catch {
      setDetailEntry(row);
    }
  };

  const handleOpenEdit = async (row: BlackListEntry) => {
    try {
      const full = await blackListApi.getById(row.id);
      setEditEntry(full);
    } catch {
      setEditEntry(row);
    }
  };

  const handleRemoveFromBlackList = (row: BlackListEntry) => {
    setConfirmRemoveEntry(row);
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemoveEntry) return;
    setIsRemoveLoading(true);
    try {
      await blackListApi.remove(confirmRemoveEntry.id);
      setConfirmRemoveEntry(null);
      fetchEntries();
      setToastState({
        variant: "success",
        message: t("blackList.toast.removeSuccess"),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error removing from black list:", error);
    } finally {
      setIsRemoveLoading(false);
    }
  };

  const handleFormSubmit = async (data: BlackListFormValues, id?: string) => {
    try {
      // Modal içinde uploadToBackend ile yüklenmiş fileIds varsa onu kullan, yoksa eski akışta supportingDocuments'ı yükle
      let fileIds: string[] | undefined = data.fileIds?.length
        ? data.fileIds
        : undefined;
      if (!fileIds && data.supportingDocuments?.length) {
        const uploaded = await uploadAdminFiles(data.supportingDocuments);
        fileIds = uploaded.map((f) => f.id);
      }

      if (id) {
        await blackListApi.update(id, {
          reason: data.reason,
          startDate: data.startDate,
          endDate: data.endDate,
          description: data.description || undefined,
          fileIds,
        });
        setToastState({
          variant: "success",
          message: t("blackList.toast.updateSuccess"),
        });
      } else {
        await blackListApi.create({
          userId: data.personnelId,
          reason: data.reason,
          startDate: data.startDate,
          endDate: data.endDate,
          description: data.description || undefined,
          fileIds,
        });
        setToastState({
          variant: "success",
          message: t("blackList.toast.createSuccess"),
        });
      }
      setEditEntry(null);
      setIsAddDrawerOpen(false);
      fetchEntries();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving black list entry:", error);
    }
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
  };

  const sortOptions: SortOption[] = [
    { label: t("blackList.sort.newest"), value: "createdAt_desc" },
    { label: t("blackList.sort.oldest"), value: "createdAt_asc" },
    {
      label: t("blackList.sort.startDateDesc"),
      value: "startDate_desc",
    },
    {
      label: t("blackList.sort.startDateAsc"),
      value: "startDate_asc",
    },
  ];

  const handleSortChange = (option: SortOption) => {
    const [field, order] = (option.value as string).split("_") as [
      "startDate" | "endDate" | "bannedAt" | "createdAt",
      "asc" | "desc",
    ];
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const filterFields: FilterField[] = [
    {
      name: "reason",
      label: t("blackList.filters.reason"),
      type: "dropdown",
      placeholder: t("blackList.filters.reasonPlaceholder"),
      options: [
        { label: t("blackList.absence"), value: "Devamsızlık" },
        { label: t("blackList.failure"), value: "Başarısızlık" },
      ],
      searchable: false,
      multiple: false,
    },
  ];

  const columns: TableColumn<BlackListEntry>[] = [
    {
      header: t("blackList.columns.bannedPersonName"),
      accessor: (row) => row.bannedPersonName ?? row.personnelName,
      className: "banned-person-name-column",
    },
    {
      header: t("blackList.columns.bannedPersonId"),
      accessor: "personnelId",
      className: "banned-person-id-column",
    },
    {
      header: t("blackList.columns.bannedByPersonName"),
      accessor: (row) => row.bannedByPersonName ?? "—",
      className: "banned-by-person-name-column",
    },
    {
      header: t("blackList.columns.startDate"),
      accessor: "startDate",
      className: "start-date-column",
    },
    {
      header: t("blackList.columns.endDate"),
      accessor: "endDate",
      className: "end-date-column",
    },
    {
      header: t("blackList.columns.reason"),
      accessor: (row) => (
        <span className={styles.reasonChip}>
          <Chip
            type={row.reason === "Devamsızlık" ? "error" : "warning"}
            variant="soft"
            size="small"
          >
            {row.reason === "Devamsızlık"
              ? t("blackList.absence")
              : t("blackList.failure")}
          </Chip>
        </span>
      ),
      className: "reason-column",
    },
  ];

  const renderActions = (row: BlackListEntry) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuItems = [
      {
        label: t("blackList.actions.detail"),
        icon: <EyeIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          handleOpenDetail(row);
        },
      },
      canUpdate && {
        label: t("blackList.actions.edit"),
        icon: <EditIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          handleOpenEdit(row);
        },
      },
      canDelete && {
        label: t("blackList.actions.removeFromList"),
        icon: <EditIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          handleRemoveFromBlackList(row);
        },
      },
    ].filter(Boolean) as {
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
    }[];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      {toastState && (
        <div className={styles.toastWrapper}>
          <Toast
            variant={toastState.variant}
            message={toastState.message}
            title={toastState.title}
            onClose={() => setToastState(null)}
            duration={5000}
          />
        </div>
      )}
      <Header title={t("blackList.title")} />

      <PageHeader
        searchPlaceholder={t("blackList.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? handleAddToBlackList : undefined}
        addButtonLabel={
          canCreate
            ? t("blackList.addButton")
            : undefined
        }
      />

      <BlackListDetailDrawer
        isOpen={!!detailEntry}
        onClose={() => setDetailEntry(null)}
        entry={detailEntry}
        onEdit={
          canUpdate
            ? (entry) => {
                setDetailEntry(null);
                handleOpenEdit(entry);
              }
            : undefined
        }
        onRemoveFromBlackList={
          canDelete
            ? (entry) => {
                setDetailEntry(null);
                handleRemoveFromBlackList(entry);
              }
            : undefined
        }
      />

      <BlackListFormDrawer
        isOpen={!!editEntry}
        onClose={() => setEditEntry(null)}
        mode="edit"
        entry={editEntry}
        onSubmit={handleFormSubmit}
      />

      <BlackListFormDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        mode="add"
        entry={null}
        onSubmit={handleFormSubmit}
      />

      <ConfirmModal
        isOpen={!!confirmRemoveEntry}
        onClose={() => setConfirmRemoveEntry(null)}
        onConfirm={handleConfirmRemove}
        title={t("blackList.confirmRemove.title")}
        message={
          confirmRemoveEntry ? (
            <span>
              <span className={styles.confirmModalName}>
                {confirmRemoveEntry.personnelName}
              </span>
              {t("blackList.confirmRemove.messageSuffix")}
            </span>
          ) : (
            ""
          )
        }
        confirmText={t("buttons.confirm")}
        cancelText={t("buttons.cancel")}
        loading={isRemoveLoading}
      />

      <Table
        columns={columns}
        data={entries}
        isLoading={isLoading}
        selectedRows={selectedRows}
        onRowSelect={handleRowSelect}
        onSelectAll={handleSelectAll}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        actionsColumnHeader={t("blackList.columns.actions")}
        openDropdowns={openDropdowns}
        emptyMessage={t("blackList.noData")}
      />
    </DashboardLayout>
  );
};

export default BlackListPage;
